import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Edit2, Trash2, Plus, Search, Map, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';

function ZonesList() {
  const [zones, setZones] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [provinces, setProvinces] = useState<any[]>([]);

  useEffect(() => {
    fetchZones();
    fetchProvinces();
  }, []);

  const fetchZones = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('zones')
        .select(`
          *,
          provinces(*)
        `)
        .order('name', { ascending: true });
      
      if (error) {
        console.error('Error fetching zones:', error);
        toast.error('Errore nel caricamento delle zone');
      } else {
        setZones(data || []);
      }
    } catch (error) {
      console.error('Error fetching zones:', error);
      toast.error('Errore nel caricamento delle zone');
    } finally {
      setLoading(false);
    }
  };

  const fetchProvinces = async () => {
    try {
      const { data, error } = await supabase
        .from('provinces')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) {
        console.error('Error fetching provinces:', error);
      } else {
        setProvinces(data || []);
      }
    } catch (error) {
      console.error('Error fetching provinces:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa zona?')) return;
    
    try {
      // Check if the zone is being used in any property
      const { count, error: countError } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('zone_id', id);
      
      if (countError) {
        throw countError;
      }
      
      if (count && count > 0) {
        toast.warning(`Questa zona è utilizzata in ${count} immobili. Rimuovi prima l'associazione.`);
        return;
      }
      
      const { error } = await supabase
        .from('zones')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast.success('Zona eliminata con successo');
      setZones(zones.filter(zone => zone.id !== id));
    } catch (error: any) {
      console.error('Error deleting zone:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante l\'eliminazione'}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleProvinceFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProvinceFilter(e.target.value);
  };

  const filteredZones = zones.filter(zone => {
    const matchesSearch = zone.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         zone.provinces?.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProvince = !provinceFilter || zone.province_id === provinceFilter;
    return matchesSearch && matchesProvince;
  });

  // Group zones by province
  const groupedZones = filteredZones.reduce((acc, zone) => {
    const provinceId = zone.province_id;
    const provinceName = zone.provinces?.name || 'Sconosciuta';
    
    if (!acc[provinceId]) {
      acc[provinceId] = {
        name: provinceName,
        zones: []
      };
    }
    
    acc[provinceId].zones.push(zone);
    return acc;
  }, {} as { [key: string]: { name: string, zones: any[] } });

  return (
    <AdminLayout title="Gestione Zone">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl font-semibold">Zone</h2>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <Link
            to="/admin/provinces"
            className="bg-secondary hover:bg-secondary-light text-white px-4 py-2.5 rounded-md transition-colors flex items-center justify-center"
          >
            <Building2 className="w-5 h-5 mr-2" />
            Gestisci Comuni
          </Link>
          <Link
            to="/admin/zones/new"
            className="bg-primary hover:bg-primary-light text-white px-4 py-2.5 rounded-md transition-colors flex items-center justify-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuova Zona
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Cerca zona..."
              className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          
          <div>
            <select
              value={provinceFilter}
              onChange={handleProvinceFilter}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Tutte le città/comuni</option>
              {provinces.map(province => (
                <option key={province.id} value={province.id}>
                  {province.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento zone...</p>
        </div>
      ) : filteredZones.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Map className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna zona trovata</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || provinceFilter
              ? 'Nessuna zona corrisponde ai criteri di ricerca.' 
              : 'Non ci sono ancora zone. Crea la tua prima zona.'}
          </p>
          <Link
            to="/admin/zones/new"
            className="inline-flex items-center bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-md transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuova Zona
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.values(groupedZones).map(({ name: provinceName, zones }) => (
            <div key={provinceName} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b">
                <h3 className="font-semibold text-gray-800">Comune di {provinceName}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 text-sm uppercase">
                      <th className="py-3 px-6">Nome Zona</th>
                      <th className="py-3 px-6 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((zone) => (
                      <tr key={zone.id} className="border-t hover:bg-gray-50">
                        <td className="py-4 px-6">
                          <div className="font-medium">{zone.name}</div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex justify-end space-x-2">
                            <Link
                              to={`/admin/zones/${zone.id}/edit`}
                              className="text-blue-600 hover:text-blue-700"
                              title="Modifica"
                            >
                              <Edit2 className="w-5 h-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(zone.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Elimina"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export default ZonesList;